import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";

// How often to check for a newer deployed version.
const POLL_INTERVAL_MS = 60_000;

/** URL of the always-fresh version file, respecting the app's public path. */
const versionUrl = (): string => `${process.env.PUBLIC_URL || ""}/version.json`;

/**
 * Detects when a new version of the app has been deployed and forces the user to
 * reload, so nobody keeps running a stale cached bundle after a deploy.
 *
 * It records the version the tab loaded with, then polls `/version.json`
 * (every minute + whenever the tab regains focus). When the deployed version no
 * longer matches, it shows a non-dismissible "reload to update" modal.
 *
 * Fails open: any network/parse error is ignored and never blocks the user, and
 * if `version.json` is missing the feature is simply inert.
 */
const UpdateGate: React.FC = () => {
  const { t, isRTL } = useLanguage();
  const initialVersion = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let active = true;

    const check = async () => {
      try {
        const res = await fetch(`${versionUrl()}?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        const version = data?.version;
        if (!version || typeof version !== "string") return;

        if (initialVersion.current === null) {
          initialVersion.current = version;
        } else if (version !== initialVersion.current && active) {
          setUpdateAvailable(true);
        }
      } catch {
        // Non-fatal — never block the app on a failed version check.
      }
    };

    check();
    const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, []);

  if (!updateAvailable) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          maxWidth: 420,
          width: "100%",
          padding: "1.75rem",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 44, marginBottom: 8 }}>🔄</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 8px" }}>
          {t("update_available_title") || "A new version is available"}
        </h2>
        <p style={{ fontSize: 14, color: "#4b5563", margin: "0 0 20px" }}>
          {t("update_available_message") ||
            "The app has been updated. Please reload to get the latest version."}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            width: "100%",
            padding: "12px 16px",
            borderRadius: 12,
            border: "none",
            background: "#2E5D7D",
            color: "#fff",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {t("reload_now") || "Reload Now"}
        </button>
      </div>
    </div>
  );
};

export default UpdateGate;
